export interface EligibilityApiResponseEligibilityRequest {
  controlNumber?: string;
  tradingPartnerServiceId?: string;
  provider?: ProviderDto;
  subscriber?: SubscriberDto;
}

export interface PayerDetails {
  payer_name?: string;
  address?: Address;
}

export interface Address {
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  countryCode?: string;
  locationIdentifier?: string;
  countrySubDivisionCode?: string;
}

export interface PlanDetails {
  plan_number?: string;
  group_name?: string;
  group_number?: string;
  plan_start_date?: string;
  plan_eligibility_start_date?: string;
  plan_name?: string;
  plan_active?: boolean;
}

export interface Service {
  co_insurance_in_network?: CoInsurance;
  co_payment_in_network?: CoPayment;
  service_code?: string;
}

interface ServiceClass {
  coInsuranceInNetwork?: CoInsurance;
  coPaymentInNetwork?: CoPayment;
  serviceCode?: string;
}

export interface CoInsurance {
  amount?: string;
}

export interface CoPayment {
  amount?: string;
}

export interface CoverageSummary {
  individual_deductible_in_network?: Deductible;
  individual_oop_in_network?: OOP;
  family_deductible_in_network?: Deductible;
  family_oop_in_network?: OOP;
}

export interface Deductible {
  total_amount?: string;
  remaining_amount?: string;
}
export interface OOP {
  total_amount?: string;
  remaining_amount?: string;
}

export interface EligibilityInfo {
  provider: ProviderDto;
  subscriber: SubscriberDto;
}

export type ProviderDto = IndividualProvider | OrganizationProvider;

interface IndividualProvider {
  firstName: string;
  lastName: string;
  organizationName?: never;
  npi: string;
}

interface OrganizationProvider {
  firstName?: never;
  lastName?: never;
  organizationName: string;
  npi: string;
}

export interface SubscriberDto {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
}

export interface EligibilityApiResponse {
  eligibility_id: string;
  state: string;
  card_id: string;
  created_at: string;
  update_at: string;
  eligibility_request?: EligibilityApiResponseEligibilityRequest;
  eligibility_response?: Response;
  eligibility_summarized_response?: EligibilitySummarizedResponse;
}

export interface EligibilitySummarizedResponse {
  eligibility_request_id?: string;
  subscriber_details?: SubscriberDetails;
  payer_details?: PayerDetails;
  plan_details?: PlanDetails;
  coverage_summary?: CoverageSummary;
  chiropractic?: Service;
  emergency_room?: Service;
  office_visit?: Service;
  urgent_care?: Service;
  hospital_outpatient?: Service;
}

export interface SubscriberDetails {
  member_id?: string;
  firstname?: string;
  lastname?: string;
  middlename?: string;
  gender?: string;
  address?: Address;
  dob?: string;
}

export default class Eligibility {
  eligibilityId: string;
  state: string;
  cardId: string;
  createdAt: string;
  updateAt: string;
  eligibilityRequest?: EligibilityApiResponseEligibilityRequest;
  eligibilityResponse?: Response;
  eligibilitySummarizedResponse?: {
    eligibilityRequestId?: string;
    subscriberDetails?: {
      memberId?: string;
      firstName?: string;
      lastName?: string;
      middleName?: string;
      gender?: string;
      dob?: string;
      address?: Address;
    };
    payerDetails?: {
      payerName?: string;
      address?: Address;
    };
    planDetails?: {
      planNumber?: string;
      groupName?: string;
      groupNumber?: string;
      planStartDate?: string;
      planEligibilityStartDate?: string;
      planName?: string;
      planActive?: boolean;
    };
    coverageSummary?: {
      individualDeductibleInNetwork?: {
        totalAmount?: string;
        remainingAmount?: string;
      };
      individualOOPInNetwork?: {
        totalAmount?: string;
        remainingAmount?: string;
      };
      familyDeductibleInNetwork?: {
        totalAmount?: string;
        remainingAmount?: string;
      };
      familyOOPInNetwork?: {
        totalAmount?: string;
        remainingAmount?: string;
      };
    };
    chiropractic?: ServiceClass;
    emergencyRoom?: ServiceClass;
    officeVisit?: ServiceClass;
    urgentCare?: ServiceClass;
    hospitalOutpatient?: ServiceClass;
  };

  public constructor(params: EligibilityApiResponse) {
    this.eligibilityId = params.eligibility_id;
    this.state = params.state;
    this.cardId = params.card_id;
    this.createdAt = params.created_at;
    this.updateAt = params.update_at;
    this.eligibilityRequest = params.eligibility_request
      ? params.eligibility_request
      : undefined;
    this.eligibilityResponse = params.eligibility_response;

    if (params.eligibility_summarized_response) {
      const esr = params.eligibility_summarized_response; // For brevity

      this.eligibilitySummarizedResponse = {
        eligibilityRequestId: esr.eligibility_request_id,
        subscriberDetails: esr.subscriber_details
          ? {
              memberId: esr.subscriber_details.member_id,
              firstName: esr.subscriber_details.firstname,
              lastName: esr.subscriber_details.lastname,
              middleName: esr.subscriber_details.middlename,
              gender: esr.subscriber_details.gender,
              dob: esr.subscriber_details.dob,
              address: esr.subscriber_details.address,
            }
          : undefined,
        payerDetails: esr.payer_details
          ? {
              payerName: esr.payer_details.payer_name,
              address: esr.payer_details.address,
            }
          : undefined,
        planDetails: esr.plan_details
          ? {
              planNumber: esr.plan_details.plan_number,
              groupName: esr.plan_details.group_name,
              groupNumber: esr.plan_details.group_number,
              planStartDate: esr.plan_details.plan_start_date,
              planEligibilityStartDate:
                esr.plan_details.plan_eligibility_start_date,
              planName: esr.plan_details.plan_name,
              planActive: esr.plan_details.plan_active,
            }
          : undefined,
        coverageSummary: esr.coverage_summary
          ? {
              individualDeductibleInNetwork: {
                totalAmount:
                  esr.coverage_summary.individual_deductible_in_network
                    ?.total_amount,
                remainingAmount:
                  esr.coverage_summary.individual_deductible_in_network
                    ?.remaining_amount,
              },
              individualOOPInNetwork: {
                totalAmount:
                  esr.coverage_summary.individual_oop_in_network?.total_amount,
                remainingAmount:
                  esr.coverage_summary.individual_oop_in_network
                    ?.remaining_amount,
              },
              familyDeductibleInNetwork: {
                totalAmount:
                  esr.coverage_summary.family_deductible_in_network
                    ?.total_amount,
                remainingAmount:
                  esr.coverage_summary.family_deductible_in_network
                    ?.remaining_amount,
              },
              familyOOPInNetwork: {
                totalAmount:
                  esr.coverage_summary.family_oop_in_network?.total_amount,
                remainingAmount:
                  esr.coverage_summary.family_oop_in_network?.remaining_amount,
              },
            }
          : undefined,
        chiropractic: this.mapService(esr.chiropractic),
        emergencyRoom: this.mapService(esr.emergency_room),
        officeVisit: this.mapService(esr.office_visit),
        urgentCare: this.mapService(esr.urgent_care),
        hospitalOutpatient: this.mapService(esr.hospital_outpatient),
      };
    }
  }

  // Helper function to map service details
  private mapService(service?: Service): ServiceClass | undefined {
    return service
      ? {
          coInsuranceInNetwork: {
            amount: service.co_insurance_in_network?.amount,
          },
          coPaymentInNetwork: {
            amount: service.co_payment_in_network?.amount,
          },
          serviceCode: service.service_code,
        }
      : undefined;
  }
}
